/**
 * NoTranslateWords — runs once at app boot and wraps legal acronyms /
 * proper nouns in <span class="notranslate" translate="no"> so Google
 * Translate leaves them alone. Idempotent; safe to run again after
 * route changes.
 *
 * Why do this at runtime rather than in every JSX file:
 *   • The app has 100+ pages/components. Hand-wrapping every mention of
 *     "TAL" or "Loi 41" would touch dozens of files.
 *   • Google Translate only respects `translate="no"` on elements that
 *     exist BEFORE it starts translating. This module runs on every
 *     route change to catch new content.
 */
const TERMS = [
  // Landlord/tenant tribunals
  "LTB", "TAL", "RTB", "RTDRS",
  // Legal acronyms
  "RTA", "RBQ",
  // Quebec laws (case-sensitive multi-word)
  "Loi 41", "Loi 25",
  // Ontario N-forms + L-forms
  "N1", "N2", "N4", "N5", "N6", "N7", "N8", "N9", "N11", "N12", "N13", "L1", "L2",
  // Federal / tax
  "T776", "T5013", "NR4", "CCA", "CMHC", "CRA",
  // Brand names
  "Central Rentals", "Central Rentals Canada",
  "Stripe", "Butterfly MX", "CondoWorks", "Resend", "Twilio",
];

// Order matters: multi-word phrases must match before their single-word substrings.
const SORTED = [...TERMS].sort((a, b) => b.length - a.length);
const PATTERN = new RegExp(
  "\\b(" +
    SORTED
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|") +
  ")\\b",
  "g",
);

// Only touch these element types to avoid corrupting <input>/<script>/<code> etc.
const TEXT_HOSTS = new Set([
  "P", "SPAN", "DIV", "LI", "DD", "DT", "TD", "TH",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "STRONG", "EM", "B", "I", "SMALL", "LABEL",
]);

function protectTerms(root = document.body) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      if (!TEXT_HOSTS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      // Already inside a notranslate ancestor → skip.
      if (p.closest && p.closest(".notranslate,[translate='no']")) {
        return NodeFilter.FILTER_REJECT;
      }
      // Google Translate injects <font> tags — those contain the
      // translated content and we should leave them alone.
      if (p.closest && p.closest("font[style*='vertical-align']")) {
        return NodeFilter.FILTER_REJECT;
      }
      if (!PATTERN.test(node.nodeValue || "")) {
        PATTERN.lastIndex = 0;
        return NodeFilter.FILTER_REJECT;
      }
      PATTERN.lastIndex = 0;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const targets = [];
  let n;
  while ((n = walker.nextNode())) targets.push(n);
  for (const node of targets) {
    const text = node.nodeValue || "";
    PATTERN.lastIndex = 0;
    const parts = [];
    let last = 0;
    let m;
    while ((m = PATTERN.exec(text))) {
      if (m.index > last) parts.push({ t: text.slice(last, m.index), keep: false });
      parts.push({ t: m[0], keep: true });
      last = m.index + m[0].length;
    }
    if (parts.length === 0) continue;
    if (last < text.length) parts.push({ t: text.slice(last), keep: false });
    const frag = document.createDocumentFragment();
    for (const p of parts) {
      if (!p.keep) {
        frag.appendChild(document.createTextNode(p.t));
      } else {
        const s = document.createElement("span");
        s.setAttribute("translate", "no");
        s.className = "notranslate";
        s.textContent = p.t;
        frag.appendChild(s);
      }
    }
    node.parentNode?.replaceChild(frag, node);
  }
}

let installed = false;
export function installLegalTermProtection() {
  if (typeof window === "undefined" || installed) return;
  installed = true;
  const run = () => protectTerms(document.body);
  // Initial pass after first paint.
  if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(run, 0);
  } else {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  }
  // React swaps DOM constantly — batch-run every 800ms on quiet phases
  // rather than reacting to every single mutation.
  let queued = false;
  const obs = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    setTimeout(() => { queued = false; run(); }, 800);
  });
  obs.observe(document.body, { childList: true, subtree: true });
}
